// pipe: frontend wave (F6) — the PURE, prop-only storefront Catalog view (the
// Vitest-tested seam).
//
// Import discipline (mirrors the portal's orders-view.tsx): this file imports
// ONLY @affine/i18n (mocked under happy-dom), a TYPE-ONLY import from the
// storefront api service (erased by swc — no runtime load), and the css module.
// It must NOT pull the runtime StorefrontApiService, the auth/PKCE plumbing,
// react-router, or any DI. The container (catalog.tsx) owns all of that.
import { useI18n } from '@affine/i18n';
import { memo } from 'react';

import type { StorefrontRecord } from '../services/storefront-api';
import * as styles from './storefront.css';

export interface CatalogViewProps {
  /** The public catalog page(s) (Storefront positive-allowlist projection). */
  rows: StorefrontRecord[];
  loading?: boolean;
  error?: string | null;
  /** The last page was full — more rows may exist. */
  mayHaveMore?: boolean;
  /** Open a product's detail screen. */
  onOpen: (id: string) => void;
  /** Fetch the next page. */
  onLoadMore?: () => void;
}

// `data` is the server's positive allowlist — read every field DEFENSIVELY
// (headline/ask_price/currency/condition/sku/quantity_available only; internal
// fields do not exist on this surface).
function str(v: unknown): string | null {
  return v === undefined || v === null ? null : String(v);
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

// Hoisted formatters — Intl.NumberFormat construction is expensive, so build
// one plain-number instance up front and one instance per currency code,
// lazily, instead of a fresh constructor per card render.
const plainNumberFormat = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
});
const currencyFormats = new Map<string, Intl.NumberFormat>();

/** Customer-facing price via Intl/CLDR; `en-US` keeps assertions stable. */
export function formatPrice(
  price: number | null,
  currency: string | null
): string | null {
  if (price === null) {
    return null;
  }
  if (currency) {
    try {
      let format = currencyFormats.get(currency);
      if (!format) {
        format = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency,
        });
        currencyFormats.set(currency, format);
      }
      return format.format(price);
    } catch {
      // unknown currency code — fall through to the plain number
    }
  }
  return plainNumberFormat.format(price);
}

export function CatalogView(props: CatalogViewProps) {
  const t = useI18n();

  if (props.error) {
    return (
      <div className={styles.body}>
        <div className={styles.errorPanel} role="alert">
          {t['com.affine.storefront.catalog.error']()}
        </div>
      </div>
    );
  }

  if (props.rows.length === 0) {
    return (
      <div className={styles.body}>
        <h1 className={styles.title}>
          {t['com.affine.storefront.catalog.title']()}
        </h1>
        <div className={styles.statusPanel} role="status">
          {props.loading
            ? t['com.affine.storefront.catalog.loading']()
            : t['com.affine.storefront.catalog.empty']()}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.body}>
      <h1 className={styles.title}>
        {t['com.affine.storefront.catalog.title']()}
      </h1>
      <div className={styles.cardGrid}>
        {props.rows.map(row => (
          <CatalogCard key={row.id} row={row} onOpen={props.onOpen} />
        ))}
      </div>
      {props.mayHaveMore && props.onLoadMore ? (
        <div className={styles.loadMoreWrap}>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={props.onLoadMore}
            disabled={Boolean(props.loading)}
          >
            {props.loading
              ? t['com.affine.storefront.catalog.loading']()
              : t['com.affine.storefront.catalog.load-more']()}
          </button>
        </div>
      ) : null}
    </div>
  );
}

// One product card, memoized (`onOpen` is a stable container useCallback): a
// Load-more append re-renders only the NEW cards, never every earlier one.
// Same markup as before.
const CatalogCard = memo(function CatalogCard({
  row,
  onOpen,
}: {
  row: StorefrontRecord;
  onOpen: (id: string) => void;
}) {
  const t = useI18n();
  const headline = str(row.data.headline) ?? row.title ?? row.id.slice(0, 8);
  const price = formatPrice(num(row.data.ask_price), str(row.data.currency));
  const condition = str(row.data.condition);
  const qty = num(row.data.quantity_available);
  return (
    <a
      className={styles.card}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(row.id)}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          onOpen(row.id);
        }
      }}
    >
      <span className={styles.cardHeadline}>{headline}</span>
      {price ? <span className={styles.cardPrice}>{price}</span> : null}
      <span className={styles.cardMeta}>
        {condition ? <span>{condition}</span> : null}
        {qty !== null ? (
          <span>
            {t['com.affine.storefront.catalog.available']({
              count: String(qty),
            })}
          </span>
        ) : null}
      </span>
    </a>
  );
});
